import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import jwt_decode from 'jwt-decode';
import { Card, CardBody, Button, Row, Col } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';

import { AddEditAvailabilityModal } from './ActionModals/AddEditAvailabilityModal';
import { DeleteAvailabilityConfirmationModal } from './ActionModals/DeleteAvailabilityConfirmationModal';

import {
  getAllAvailabilities,
  createAvailability,
  updateAvailability,
  deleteAvailability,
} from './Helpers/Api';

import {
  getValidationSchema,
  getInitialValues,
} from './Helpers/Config';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const formatMinutes = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const AvailabilityManagement = () => {
  const dispatch = useDispatch();

  const [availability, setAvailability] = useState(null);
  const [modal, setModal] = useState('');
  const [user, setUser] = useState(null);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;
      const decoded = jwt_decode(token);
      return {
        id: decoded.id || decoded._id || decoded.sub,
        role: decoded.role
      };
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    if (!user) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { availabilities } = await getAllAvailabilities({
        person_id: user.id,
        role: user.role
      });

      // Assume user has only one availability record
      setAvailability(availabilities?.[0] || null);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch your availability.', 'error');
      setAvailability(null);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user, dispatch]);

  const formik = useFormik({
    initialValues: getInitialValues(modal, availability),
    validationSchema: getValidationSchema(modal),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!user) {
        Swal.fire('Authentication Error', 'Please login again.', 'error');
        setSubmitting(false);
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const payload = {
          ...values,
          person_id: user.id,
          role: user.role
        };

        if (modal === 'add') {
          await createAvailability(payload);
          Swal.fire('Success', 'Availability added successfully!', 'success');
        }

        if (modal === 'edit') {
          await updateAvailability(availability._id, payload);
          Swal.fire('Success', 'Availability updated successfully!', 'success');
        }

        await fetchAvailability();
        closeModal();
        resetForm();
      } catch (error) {
        Swal.fire('Error', error.message || 'Operation failed', 'error');
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = () => {
    setModal('add');
  };

  const openEditModal = () => {
    setModal('edit');
  };

  const openDeleteModal = () => {
    setModal('delete');
  };

  const closeModal = () => {
    formik.resetForm();
    setModal('');
  };

  const handleDeleteConfirm = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteAvailability(availability._id);
      Swal.fire('Deleted', 'Availability deleted successfully!', 'success');
      await fetchAvailability();
      closeModal();
    } catch (error) {
      Swal.fire('Error', error.message || 'Delete failed', 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    const currentUser = getUserFromToken();
    setUser(currentUser);
  }, [getUserFromToken]);

  useEffect(() => {
    if (user) {
      fetchAvailability();
    }
  }, [user, fetchAvailability]);

  const getTotalSlots = () => {
    if (!availability?.availability) return 0;
    return availability.availability.reduce((total, day) => total + (day.slots?.length || 0), 0);
  };

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | My Availability</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs
          title="Availabilities"
          breadcrumbItem="My Availability"
        />

        {!availability ? (
          <Card>
            <CardBody className="text-center py-5">
              <FeatherIcon icon="calendar" size={64} className="text-muted mb-3" />
              <h5 className="mb-3">No Availability Set</h5>
              <p className="text-muted mb-4">
                You haven't set your availability yet. Click the button below to get started.
              </p>
              <Button color="primary" onClick={openAddModal}>
                <FeatherIcon icon="plus" size={16} className="me-2" />
                Set My Availability
              </Button>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <p className="text-muted mb-0">
                    Total: {getTotalSlots()} time slot{getTotalSlots() !== 1 ? 's' : ''} across {availability.availability.filter(d => d.slots?.length > 0).length} day{availability.availability.filter(d => d.slots?.length > 0).length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div>
                  <Button color="primary" size="sm" onClick={openEditModal} className="me-2">
                    <FeatherIcon icon="edit" size={16} className="me-1" />
                    Edit
                  </Button>
                  <Button color="danger" size="sm" onClick={openDeleteModal}>
                    <FeatherIcon icon="trash-2" size={16} className="me-1" />
                    Delete
                  </Button>
                </div>
              </div>

              <Row>
                {DAYS.map((dayName) => {
                  const dayData = availability.availability?.find(d => d.day === dayName);
                  const hasSlots = dayData?.slots?.length > 0;

                  return (
                    <Col md={6} lg={4} key={dayName} className="mb-3">
                      <Card className={`border ${hasSlots ? 'border-primary' : 'border-light'}`}>
                        <CardBody className="p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 text-capitalize">{dayName}</h6>
                            {hasSlots && (
                              <span className="badge bg-primary">
                                {dayData.slots.length} slot{dayData.slots.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>

                          {!hasSlots ? (
                            <p className="text-muted small mb-0">Not available</p>
                          ) : (
                            <div className="mt-2">
                              {dayData.slots.map((slot, idx) => (
                                <div key={idx} className="d-flex align-items-center mb-1">
                                  <FeatherIcon icon="clock" size={14} className="text-muted me-2" />
                                  <span className="small">
                                    {formatMinutes(slot.start_time)} - {formatMinutes(slot.end_time)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </CardBody>
          </Card>
        )}

        <AddEditAvailabilityModal
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          user={user}
          onClose={closeModal}
        />

        <DeleteAvailabilityConfirmationModal
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={availability}
        />
      </div>
    </div>
  );
};

export { AvailabilityManagement };